import React, { useState, useEffect } from 'react';
import { 
  Card, CardContent, Typography, TextField, Button, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Paper, 
  Grid, Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Add, Close, Edit, Delete } from '@mui/icons-material';
import { ActionButtons } from './shared/ActionButtons';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiHelpers';

function ServerManagement({ onClose }) {
  const [servers, setServers] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [form, setForm] = useState({
    server_id: '',
    server_name: '',
    description: ''
  });
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const response = await apiGet('/api/servers');
      setServers(response.data || []);
    } catch (error) {
      console.error('Error fetching servers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    try {
      if (editingServer) {
        await apiPut(`/api/servers/${editingServer.server_id}`, {
          server_name: form.server_name,
          description: form.description
        });
      } else {
        await apiPost('/api/servers', form);
      }
      
      setForm({ server_id: '', server_name: '', description: '' });
      setAddDialogOpen(false);
      setEditingServer(null);
      fetchServers();
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Failed to save server');
    }
  };

  const handleEdit = (server) => {
    setEditingServer(server);
    setForm({
      server_id: server.server_id,
      server_name: server.server_name,
      description: server.description || ''
    });
    setAddDialogOpen(true);
  };

  const handleDelete = async (serverId) => {
    if (window.confirm('Are you sure you want to delete this server?')) {
      try {
        await apiDelete(`/api/servers/${serverId}`);
        fetchServers();
      } catch (error) {
        setErrorMessage(error.response?.data?.error || 'Failed to delete server');
      }
    }
  };

  const resetForm = () => {
    setForm({ server_id: '', server_name: '', description: '' });
    setEditingServer(null);
    setAddDialogOpen(false);
    setErrorMessage('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Server Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Server
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
          >
            Back to Monitored Accounts
          </Button>
        </Box>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {errorMessage}
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Server ID</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Server Name</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers.map((server) => (
              <TableRow key={server.server_id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{server.server_id}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{server.server_name}</TableCell>
                <TableCell>{server.description || '-'}</TableCell>
                <TableCell>{new Date(server.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleEdit(server)} 
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      onClick={() => handleDelete(server.server_id)} 
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {servers.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  No servers found. Add your first server to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Server Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={resetForm}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: 3 } }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingServer ? 'Edit Server' : 'Add New Server'}
          </Typography>
          <IconButton onClick={resetForm} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Server ID"
                  value={form.server_id}
                  onChange={(e) => setForm({...form, server_id: e.target.value})}
                  required
                  disabled={!!editingServer}
                  helperText={editingServer ? "Server ID cannot be changed" : "Unique identifier (max 25 characters)"}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                  slotProps={{
                    htmlInput: { maxLength: 25 }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Server Name"
                  value={form.server_name}
                  onChange={(e) => setForm({...form, server_name: e.target.value})}
                  required
                  helperText="Human-readable name displayed in dropdowns"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  multiline
                  rows={2}
                  helperText="Optional description"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button onClick={resetForm} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3
              }}
            >
              {editingServer ? 'Update Server' : 'Add Server'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ServerManagement;